<template>
  <!-- Mobile overlay backdrop -->
  <div 
    v-if="isMobile && !stIsSidenavCollapsed" 
    class="sidenav-backdrop d-md-none"
    @click="toggleSidenav"
  ></div>
  
  <aside id="sidenav-main" class="sidenav navbar navbar-vertical navbar-expand-xs border-0 border-radius-xl my-3 ms-3"
    :class="`${stIsRTL ? 'me-3 rotate-caret fixed-end' : 'fixed-start ms-3'
      } ${stSidebarType} ${!isMobile && stIsSidenavCollapsed ? 'sidenav-collapsed' : ''} ${isMobile && stIsSidenavCollapsed ? 'sidenav-mobile-hidden' : ''} ${isMobile && !stIsSidenavCollapsed ? 'sidenav-mobile-visible' : ''}`">
    <div class="sidenav-header d-flex align-items-center gap-4">

      <div class="m-0 navbar-brand" :class="{ 'd-none': stIsSidenavCollapsed }">
        <span class="sidenav-brand-text">Church Scheduling</span>
      </div>

      <button
        :class="!stIsSidenavCollapsed ? 'btn btn-link p-2 text-white position-absolute end-0 top-0 sidenav-toggle-btn' : 'btn btn-link text-white w-100 d-flex justify-content-center align-items-center'"
        type="button" @click="toggleSidenav" aria-label="Toggle sidebar">
        <i class="material-icons">
          {{ stIsSidenavCollapsed ? 'keyboard_arrow_right' : 'keyboard_arrow_left' }}
        </i>
      </button>

    </div>
    <hr class="horizontal light mt-0 mb-2" :class="{ 'd-none': stIsSidenavCollapsed }" />
    <sidenav-list :is-collapsed="stIsSidenavCollapsed" />
  </aside>
</template>
<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import SidenavList from "./SidenavList.vue";

const store = useStore();

// Vuex getters
const stIsRTL = computed(() => store.getters.stIsRTL);
const stSidebarType = computed(() => store.getters.stSidebarType);
const stIsSidenavCollapsed = computed(() => store.getters.stIsSidenavCollapsed);

// Mobile detection
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', () => {
    const wasMobile = isMobile.value;
    checkMobile();
    // If switching from desktop to mobile and sidenav is expanded, hide it
    if (!wasMobile && isMobile.value && !stIsSidenavCollapsed.value) {
      store.commit('toggleSidenavCollapse');
    }
    // If switching from mobile to desktop and sidenav is collapsed, expand it
    if (wasMobile && !isMobile.value && stIsSidenavCollapsed.value) {
      store.commit('toggleSidenavCollapse');
    }
  });
  
  // Initialize sidenav as hidden on mobile on first load
  if (isMobile.value && !stIsSidenavCollapsed.value) {
    store.commit('toggleSidenavCollapse');
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// Methods
const toggleSidenav = () => {
  store.commit('toggleSidenavCollapse');
};
</script>

<style scoped>
.sidenav {
  transition: width 0.3s ease, min-width 0.3s ease, transform 0.3s ease;
  width: 250px;
  min-width: 250px;
}

.sidenav-collapsed {
  width: 80px !important;
  min-width: 80px !important;
}

/* Mobile styles */
@media (max-width: 767.98px) {
  .sidenav {
    position: fixed !important;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1050 !important;
    margin: 0 !important;
    transition: transform 0.3s ease;
    width: 250px !important;
    min-width: 250px !important;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Hidden state - off screen */
  .sidenav-mobile-hidden {
    transform: translateX(-100%) !important;
  }
  
  /* Visible state - on screen */
  .sidenav-mobile-visible {
    transform: translateX(0) !important;
  }
  
  /* Default: if no mobile class, show it (for backwards compatibility) */
  .sidenav:not(.sidenav-mobile-hidden):not(.sidenav-mobile-visible) {
    transform: translateX(0) !important;
  }
  
  .sidenav-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1040;
  }
}

.sidenav-toggle-btn {
  z-index: 10;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  border: none;
  background: transparent;
}

.sidenav-toggle-btn:hover {
  opacity: 1;
}

.sidenav-toggle-btn:focus {
  box-shadow: none;
  outline: none;
}

.sidenav-brand-text {
  display: block;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #a8edea 50%, #fed6e3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  transition: opacity 0.2s ease, width 0.2s ease;
  padding-left: 0.5rem;
}

.sidenav-header {
  position: relative;
  min-height: 60px;
  display: flex;
  align-items: center;
  padding: 0.5rem;
}

/* Hide text in nav items when collapsed */
.sidenav-collapsed :deep(.nav-link-text) {
  opacity: 0 !important;
  width: 0 !important;
  overflow: hidden !important;
  transition: opacity 0.2s ease, width 0.2s ease;
  margin: 0 !important;
  padding: 0 !important;
}

.sidenav-collapsed :deep(.nav-link) {
  justify-content: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.sidenav-collapsed :deep(.sidenav-normal) {
  justify-content: center;
}
</style>

<style>
/* Global styles for main content adjustment */
.sidenav-collapsed~.main-content,
.main-content:has(~ .sidenav-collapsed) {
  margin-left: 80px !important;
  transition: margin-left 0.3s ease;
}

/* Adjust when sidebar is not collapsed */
.sidenav:not(.sidenav-collapsed)~.main-content,
.main-content:has(~ .sidenav:not(.sidenav-collapsed)) {
  margin-left: 250px !important;
  transition: margin-left 0.3s ease;
}

/* Mobile: no margin for main content */
@media (max-width: 767.98px) {
  .main-content {
    margin-left: 0 !important;
  }
}
</style>